import { useState, useRef } from "react";

const C = {
  bg: "#07090f", card: "#10141f",
  gold: "#c9a84c", gold2: "#e8c97a", rose: "#c97a8a", ice: "#7ab8cc",
  text: "#e6e0d6", muted: "#7a7a8a",
  border: "rgba(201,168,76,0.16)", border2: "rgba(201,168,76,0.35)",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  .serif { font-family: 'Cormorant Garamond', serif; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: #07090f; }
  ::-webkit-scrollbar-thumb { background: #c9a84c; }
  .nav-item { display:flex; align-items:center; gap:.6rem; padding:.48rem 1.5rem; font-size:.78rem; color:#7a7a8a; cursor:pointer; border-left:2px solid transparent; transition:all .2s; font-family:'DM Sans',sans-serif; font-weight:300; }
  .nav-item:hover { color:#e6e0d6; background:rgba(201,168,76,.04); }
  .nav-item.active { color:#c9a84c; border-left-color:#c9a84c; background:rgba(201,168,76,.07); }
  .nav-child .nav-item { font-size:.73rem; padding:.38rem 1.5rem .38rem 0; }
  .card { background:#10141f; border:1px solid rgba(201,168,76,0.16); padding:1.8rem; transition:all .3s; position:relative; overflow:hidden; }
  .card::after { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(201,168,76,.05),transparent); opacity:0; transition:opacity .3s; }
  .card:hover { border-color:rgba(201,168,76,0.35); transform:translateY(-4px); box-shadow:0 18px 50px rgba(0,0,0,.45); }
  .card:hover::after { opacity:1; }
  .card.clickable { cursor:pointer; }
  .tl-line { position:absolute; left:0; top:.5rem; bottom:0; width:1px; background:rgba(201,168,76,0.16); }
  .tl-dot { position:absolute; left:-.42rem; top:.4rem; width:.84rem; height:.84rem; border-radius:50%; background:#c9a84c; border:2px solid #07090f; }
  .writing-row { display:flex; align-items:center; gap:1.6rem; padding:1.5rem 0; border-bottom:1px solid rgba(201,168,76,0.16); cursor:pointer; transition:all .22s; }
  .writing-row:hover { padding-left:.9rem; }
  .goal-row { display:flex; align-items:flex-start; gap:1rem; padding:1rem 1.3rem; border:1px solid rgba(201,168,76,0.16); background:#10141f; cursor:pointer; transition:all .28s; }
  .goal-row:hover { border-color:rgba(201,168,76,0.35); transform:translateX(5px); }
  .goal-row.done { opacity:.5; }
  .btn { display:inline-block; padding:.65rem 1.7rem; font-size:.68rem; letter-spacing:.14em; text-transform:uppercase; border:1px solid #c9a84c; color:#c9a84c; background:transparent; cursor:pointer; transition:all .28s; font-family:'DM Sans',sans-serif; text-decoration:none; }
  .btn:hover { background:#c9a84c; color:#07090f; }
  .btn.solid { background:#c9a84c; color:#07090f; }
  .btn.solid:hover { background:#e8c97a; }
  .tag { display:inline-block; padding:.25rem .8rem; border:1px solid rgba(201,168,76,.28); font-size:.65rem; letter-spacing:.1em; text-transform:uppercase; color:#c9a84c; background:rgba(201,168,76,.05); margin:.25rem .25rem 0 0; font-family:'DM Sans',sans-serif; }
  @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
  .fade-in { animation:fadeIn .4s ease both; }
`;

const GOALS = [
  "Advance research in dynamical systems applied to neuroscience",
  "Publish consistently on Substack — Ink and All Things",
  "Make meaningful progress on Manuscript I (finish editing)",
  "Develop Manuscript II with intention",
  "Continue volunteering at Canil e Gatil de Monsanto",
  "Read more books than last year",
  "Travel somewhere completely new",
  "Finish or significantly progress the Book of Poems",
  "Submit at least one academic paper or report",
  "Deepen knowledge in computational neuroscience",
];

function Tag({ children }) {
  return <span className="tag">{children}</span>;
}

function PageHero({ eyebrow, title, subtitle }) {
  return (
    <div style={{ padding:"4.5rem 3rem 2.5rem", borderBottom:"1px solid rgba(201,168,76,0.16)", background:"radial-gradient(ellipse at 20% 60%, rgba(201,168,76,.07) 0, transparent 65%)" }}>
      {eyebrow && <p style={{ fontSize:".6rem", letterSpacing:".22em", textTransform:"uppercase", color:"#c9a84c", marginBottom:".7rem", fontFamily:"'DM Sans',sans-serif" }}>{eyebrow}</p>}
      <h1 className="serif" style={{ fontSize:"clamp(2.6rem, 5.5vw, 4.5rem)", fontWeight:300, lineHeight:1.1, color:"#e6e0d6" }}>{title}</h1>
      {subtitle && <p className="serif" style={{ fontSize:"1.05rem", fontStyle:"italic", color:"#7a7a8a", marginTop:".8rem", maxWidth:560, lineHeight:1.75 }}>{subtitle}</p>}
    </div>
  );
}

function TlItem({ date, title, place, text, tags=[], color="#c9a84c" }) {
  return (
    <div style={{ position:"relative", paddingBottom:"2.5rem", paddingLeft:"2rem" }}>
      <div className="tl-dot" style={{ background:color }} />
      {date && <div style={{ fontSize:".63rem", letterSpacing:".16em", textTransform:"uppercase", color:"#c9a84c", marginBottom:".4rem", fontFamily:"'DM Sans',sans-serif" }}>{date}</div>}
      <h3 className="serif" style={{ fontSize:"1.35rem", marginBottom:".18rem" }}>{title}</h3>
      {place && <p style={{ fontSize:".76rem", color:"#7a7a8a", marginBottom:".65rem" }}>{place}</p>}
      {text && <p style={{ fontSize:".83rem", color:"#7a7a8a", lineHeight:1.72 }} dangerouslySetInnerHTML={{ __html:text }} />}
      <div style={{ marginTop:".5rem" }}>{tags.map(t => <Tag key={t}>{t}</Tag>)}</div>
    </div>
  );
}

function Timeline({ children }) {
  return (
    <div style={{ position:"relative", paddingLeft:"2rem" }}>
      <div className="tl-line" />
      {children}
    </div>
  );
}

function ComingSoon() {
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center", padding:"5rem 1rem" }}>
      <div style={{ fontSize:"2rem", marginBottom:"1.2rem", opacity:.3 }}>✦</div>
      <h2 className="serif" style={{ fontSize:"1.6rem", color:"#7a7a8a", marginBottom:".6rem" }}>Coming soon</h2>
      <p style={{ fontSize:".84rem", color:"#7a7a8a", maxWidth:320, lineHeight:1.7 }}>This page is still being written.</p>
    </div>
  );
}

function HomePage({ nav }) {
  return (
    <div>
      <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", justifyContent:"center", padding:"6rem 3rem", background:"radial-gradient(ellipse at 15% 55%, rgba(201,168,76,.08) 0, transparent 60%)" }}>
        <p style={{ fontSize:".64rem", letterSpacing:".28em", textTransform:"uppercase", color:"#c9a84c", marginBottom:"1.3rem", fontFamily:"'DM Sans',sans-serif" }}>✦ Personal Website · Ship's Log</p>
        <h1 className="serif" style={{ fontSize:"clamp(3.5rem, 9vw, 8rem)", fontWeight:300, lineHeight:.92, marginBottom:".25em" }}>
          Mariana <em style={{ fontStyle:"italic", color:"#e8c97a" }}>Brites</em><br />Lameiro
        </h1>
        <p className="serif" style={{ fontSize:"1.05rem", fontStyle:"italic", color:"#7a7a8a", marginBottom:"2.8rem" }}>
          Mathematics · Physics · Engineering · Writing · The universe in between
        </p>
        <div style={{ maxWidth:570, background:"rgba(201,168,76,.05)", border:"1px solid rgba(201,168,76,0.16)", borderLeft:"3px solid #c9a84c", padding:"1.7rem 2rem", marginBottom:"2.2rem" }}>
          <p style={{ fontSize:".58rem", letterSpacing:".22em", textTransform:"uppercase", color:"#c9a84c", marginBottom:".65rem", fontFamily:"'DM Sans',sans-serif" }}>✦ Ship's Log</p>
          <p className="serif" style={{ fontSize:"1rem", fontStyle:"italic", color:"#c0b8ac", lineHeight:1.9 }}>
            Every time you look down at your wrist and stare into those blueish veins that have always fascinated you, I hope you think of the blood that runs inside. The same blood that contains hemoglobin (Hb), an iron-rich protein. And remember that iron has only one place where it can be naturally produced — the core of dying stars.
          </p>
        </div>
        <div style={{ display:"flex", gap:".8rem", flexWrap:"wrap" }}>
          <button className="btn solid" onClick={() => nav("about-me")}>Discover more</button>
          <a href="https://inkandallthings.substack.com" target="_blank" rel="noreferrer" className="btn">Read my writing ↗</a>
        </div>
      </div>
      <div style={{ padding:"3rem", borderTop:"1px solid rgba(201,168,76,0.16)" }}>
        <p className="serif" style={{ fontSize:"1.5rem", fontWeight:300, marginBottom:"1.6rem" }}>Explore ✦</p>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(250px, 1fr))", gap:"1.2rem" }}>
          {[["about-me","✦","About me"],["academia","🎓","Academia"],["writing","✍️","Writing"],["books","📚","Books"],["2026","🌟","2026"]].map(([id,icon,title]) => (
            <div key={id} className="card clickable" onClick={() => nav(id)}>
              <div style={{ fontSize:"1.7rem", marginBottom:".8rem" }}>{icon}</div>
              <h3 className="serif" style={{ fontSize:"1.2rem", marginBottom:".35rem" }}>{title}</h3>
              <p style={{ fontSize:".68rem", color:"#c9a84c", marginTop:".9rem" }}>Explore →</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AboutPage() {
  return (
    <div>
      <PageHero eyebrow="✦ Who I am" title={<>About <em style={{ fontStyle:"italic", color:"#e8c97a" }}>me</em></>} subtitle="A person made of stardust, equations, and ink." />
      <div style={{ padding:"2.5rem 3rem 4rem" }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"3rem", alignItems:"start" }}>
          <div>
            <div className="serif" style={{ fontSize:"1.1rem", color:"#c8c0b4", lineHeight:1.9 }}>
              <p style={{ marginBottom:"1.3rem" }}>Undergraduate student in General Studies with a focus on Mathematics, Physics and Engineering. Interested in mathematical biology, quantitative modelling and neuroscience research.</p>
              <p style={{ marginBottom:"1.3rem" }}>Seeking early-stage research experience in dynamical systems applied to biological phenomena — where the language of mathematics meets the complexity of life.</p>
              <div style={{ borderLeft:"3px solid #c9a84c", padding:"1rem 1.6rem", fontStyle:"italic", color:"#bfb7ab", margin:"1.6rem 0", background:"rgba(201,168,76,.04)" }}>
                "I've always been fascinated by the places where different disciplines collide and create something entirely new — a conversation between rigour and poetry."
              </div>
              <p>Beyond academia: a writer with two manuscripts in progress and a book of poems taking shape. A cook who rates her own dishes with brutal honesty. A traveller collecting moments across continents.</p>
            </div>
            <div style={{ marginTop:"1.6rem" }}>
              {["Mathematical Biology","Dynamical Systems","Neuroscience","Quantitative Modelling","Physics","Writing","Cooking","Travel Photography"].map(t => <Tag key={t}>{t}</Tag>)}
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1rem" }}>
            {[["🎓","Focus","Maths, Physics & Engineering"],["🔬","Research","Dynamical Systems in Biology"],["🏛️","University","University of Lisbon"],["✍️","Writing","Substack · Manuscripts · Poetry"],["🐾","Volunteering","Canil e Gatil de Monsanto"],["📍","Based in","Lisboa, Portugal"]].map(([icon,label,val]) => (
              <div key={label} className="card" style={{ padding:"1.3rem" }}>
                <div style={{ fontSize:"1.3rem", marginBottom:".45rem" }}>{icon}</div>
                <div style={{ fontSize:".56rem", letterSpacing:".17em", textTransform:"uppercase", color:"#c9a84c", marginBottom:".28rem", fontFamily:"'DM Sans',sans-serif" }}>{label}</div>
                <div style={{ fontSize:".84rem" }}>{val}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ marginTop:"3.5rem" }}>
          <h2 className="serif" style={{ fontSize:"1.7rem", fontWeight:300, marginBottom:"1.8rem" }}>Volunteering</h2>
          <Timeline>
            <TlItem date="28 November 2025" title="Canil e Gatil de Monsanto 🐾" place="Lisboa, Portugal" text="Giving time and care to animals in need at Lisbon's municipal shelter." color="#c97a8a" />
            <TlItem date="14 March 2025" title="Canil e Gatil de Monsanto 🐾" place="Lisboa, Portugal" text="First volunteering session — walking, feeding and socialising with shelter animals." color="#c97a8a" />
          </Timeline>
        </div>
      </div>
    </div>
  );
}

function AcademiaPage({ nav }) {
  return (
    <div>
      <PageHero eyebrow="✦ Research & Education" title={<em style={{ fontStyle:"italic", color:"#e8c97a" }}>Academia</em>} subtitle="Where mathematics meets biology, and curiosity becomes research." />
      <div style={{ padding:"2.5rem 3rem 4rem", display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:"1rem" }}>
        {[["academia/scholarship","🏅","Scholarship"],["academia/internships","🔬","Internships"],["academia/education","📚","Education"],["academia/projects","⚗️","Projects"]].map(([id,icon,title]) => (
          <div key={id} className="card clickable" onClick={() => nav(id)}>
            <div style={{ fontSize:"1.7rem", marginBottom:".8rem" }}>{icon}</div>
            <h3 className="serif" style={{ fontSize:"1.2rem" }}>{title}</h3>
            <p style={{ fontSize:".68rem", color:"#c9a84c", marginTop:".9rem" }}>Open →</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function InternshipsPage() {
  return (
    <div>
      <PageHero eyebrow="✦ Academia" title={<em style={{ fontStyle:"italic", color:"#e8c97a" }}>Internships</em>} subtitle="Research positions at the frontier of neuroscience and computational biology." />
      <div style={{ padding:"2.5rem 3rem 4rem" }}>
        <Timeline>
          <TlItem date="November 2025 — Present" title="IBEB — Research Intern" place="Institute of Biophysics and Biomedical Engineering · University of Lisbon · Lisboa, Portugal" text="Research internship at the Institute of Biophysics and Biomedical Engineering, University of Lisbon." tags={["Biomedical Engineering","Research"]} />
          <TlItem date="September 2025 — Present" title="LASIGE — Research Intern" place="Large-Scale Informatics Systems Laboratory · University of Lisbon · Lisboa, Portugal" color="#7ab8cc" text="Applied advanced computational methodologies, specifically leveraging Large Language Models (LLMs) and accelerometry data analysis from patients to derive predictive insights into Parkinson's disease progression.<br><br>Developing analytical, technical, and scientific writing skills within an academic research environment.<br><br>Spearheaded the data analysis phase, synthesizing complex LLM outputs to formulate robust scientific conclusions supporting the interdisciplinary understanding (Neuroscience, Cognitive Science, Computer Science) of the pathology." tags={["LLMs","Parkinson's Disease","Accelerometry","Data Analysis","Neuroscience"]} />
        </Timeline>
      </div>
    </div>
  );
}

function ProjectsPage() {
  return (
    <div>
      <PageHero eyebrow="✦ Academia" title={<em style={{ fontStyle:"italic", color:"#e8c97a" }}>Projects</em>} subtitle="From ocean plastic to European affairs — curiosity-driven, interdisciplinary work." />
      <div style={{ padding:"2.5rem 3rem 4rem" }}>
        <Timeline>
          <TlItem date="January 2024 — July 2024" title="Ulisses Project" place="University of Lisbon · Lisboa, Portugal" text="Developed and proposed a collaborative, interdisciplinary solution (Science, Technology, Humanities) to a complex hypothetical real-world catastrophe — ocean plastic spillage.<br><br>Integrated critical thinking and research skills to drive problem-solving initiatives. Developed teamwork, communication, and project management skills in an academic, research-focused environment." tags={["Interdisciplinary","Environmental Science","Research"]} />
          <TlItem date="September 2024 — February 2025" title="SET — Business and Technology Week Organiser" place="External Relations · Lisboa, Portugal" color="#7ab8cc" text="Engaged in a university-wide event connecting students with innovation and industry leaders.<br><br>Secured and managed corporate participation by pitching, negotiating pricing, and managing all written and email communications with external companies." tags={["B2B Negotiation","External Relations","Event Management"]} />
          <TlItem date="September 2024 — September 2025" title="European Studies Student Nucleus — Member" place="Lisboa, Portugal" color="#c97a8a" text="Contributed to projects promoting European affairs, cultural dialogue, and international collaboration within the university community." tags={["European Affairs","Cultural Dialogue"]} />
          <TlItem date="Ongoing" title="Diferencial & O Cola — Student Journalist" place="Student Publications" color="#7aaa8c" text="Contributed to the student journalistic output of two nuclei (Diferencial e O Cola) by writing several articles and managing the editorial quality of published texts." tags={["Journalism","Editorial","Writing"]} />
        </Timeline>
      </div>
    </div>
  );
}

function WritingPage({ nav }) {
  return (
    <div>
      <PageHero eyebrow="✦ Words & worlds" title={<>My <em style={{ fontStyle:"italic", color:"#e8c97a" }}>Writing</em></>} subtitle="Two manuscripts underway, a poetry collection forming, and essays about ink and all things." />
      <div style={{ padding:"2.5rem 3rem 4rem" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:"2rem", flexWrap:"wrap", padding:"1.7rem 2rem", background:"rgba(201,168,76,.06)", border:"1px solid rgba(201,168,76,0.16)", marginBottom:"2.5rem" }}>
          <div>
            <h3 className="serif" style={{ fontSize:"1.4rem", marginBottom:".25rem" }}>Ink and All Things ✦ Substack</h3>
            <p style={{ fontSize:".77rem", color:"#7a7a8a" }}>October 2024 — Present · Essays, reflections, and the written life</p>
          </div>
          <a href="https://inkandallthings.substack.com" target="_blank" rel="noreferrer" className="btn">Read on Substack →</a>
        </div>
        {[["writing/manuscript-i","01","Manuscript I","Long-form prose · In progress · Editing phase","Manuscript"],["writing/manuscript-ii","02","Manuscript II","Long-form prose · In progress","Manuscript"],["writing/book-of-poems","03","Book of Poems","Poetry collection · In progress","Poetry"],[null,"04","Estado com Arte — Magazine","Published contributions","Magazine"]].map(([id,num,title,sub,badge]) => (
          <div key={num} className="writing-row" onClick={() => id && nav(id)} style={{ cursor:id ? "pointer" : "default" }}>
            <span className="serif" style={{ fontSize:".88rem", color:"#7a7a8a", minWidth:"1.8rem" }}>{num}</span>
            <div style={{ flex:1 }}>
              <div className="serif" style={{ fontSize:"1.35rem" }}>{title}</div>
              <div style={{ fontSize:".73rem", color:"#7a7a8a" }}>{sub}</div>
            </div>
            <span style={{ marginLeft:"auto", padding:".22rem .7rem", border:"1px solid rgba(201,168,76,0.16)", fontSize:".6rem", letterSpacing:".1em", textTransform:"uppercase", color:"#7a7a8a", whiteSpace:"nowrap", fontFamily:"'DM Sans',sans-serif" }}>{badge}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ManuscriptIPage() {
  return (
    <div>
      <PageHero eyebrow="✦ Writing" title={<>Manuscript <em style={{ fontStyle:"italic", color:"#e8c97a" }}>I</em></>} subtitle="A diary of editing — holding myself accountable, chapter by chapter." />
      <div style={{ padding:"2.5rem 3rem 4rem" }}>
        <div className="serif" style={{ fontSize:"1.1rem", color:"#c8c0b4", lineHeight:1.9, maxWidth:660 }}>
          <p style={{ marginBottom:"1.3rem" }}>Once you complete your first draft the next obvious step is to edit it. Seems straightforward, right? But it isn't so easy.</p>
          <div style={{ borderLeft:"3px solid #c9a84c", padding:"1.1rem 1.7rem", fontStyle:"italic", color:"#bfb7ab", margin:"1.6rem 0", background:"rgba(201,168,76,.04)" }}>
            "You have to go through the cringy non-sense you just wrote, and thought was the best thing in the world, only to find out that the few things out of this world in there are the grammatical mistakes you made during your 3 am session."
          </div>
          <p style={{ marginBottom:"1.3rem" }}>So yeah, it's pretty rough. Reason why I'm keeping myself accountable with this space.</p>
          <p>For the foreseeable future the plan is: use this to write a brief paragraph, after editing each chapter, about how I think it went, the things I'm happy about and the things I still think need improvement...</p>
        </div>
      </div>
    </div>
  );
}

function BooksPage({ nav }) {
  return (
    <div>
      <PageHero eyebrow="✦ A reader's archive" title={<em style={{ fontStyle:"italic", color:"#e8c97a" }}>Books</em>} />
      <div style={{ padding:"2.5rem 3rem 4rem", display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:"1.3rem" }}>
        {[["books/read","📖","Books I've Read"],["books/tbr","📋","My TBR"],["books/interesting","💡","And overall things I read and found interesting!"]].map(([id,icon,title]) => (
          <div key={id} className="card clickable" style={{ textAlign:"center", padding:"2.5rem 1.8rem" }} onClick={() => nav(id)}>
            <div style={{ fontSize:"3rem", marginBottom:"1.2rem" }}>{icon}</div>
            <h3 className="serif" style={{ fontSize:"1.25rem" }}>{title}</h3>
          </div>
        ))}
      </div>
    </div>
  );
}

function Goals2026Page() {
  const [done, setDone] = useState({});
  const toggle = i => setDone(d => ({ ...d, [i]:!d[i] }));
  const count = Object.values(done).filter(Boolean).length;
  return (
    <div>
      <PageHero eyebrow="✦ A new chapter" title={<em style={{ fontStyle:"italic", color:"#e8c97a" }}>2026</em>} subtitle="A fresh year. Aspirations written down become commitments." />
      <div style={{ padding:"2.5rem 3rem 4rem" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"1rem", marginBottom:"2rem", maxWidth:680 }}>
          <div style={{ fontSize:".65rem", letterSpacing:".18em", textTransform:"uppercase", color:"#c9a84c", fontFamily:"'DM Sans',sans-serif", whiteSpace:"nowrap" }}>{count}/{GOALS.length} done</div>
          <div style={{ flex:1, height:2, background:"rgba(201,168,76,0.16)", borderRadius:1 }}>
            <div style={{ height:"100%", width:`${(count/GOALS.length)*100}%`, background:"#c9a84c", transition:"width .4s", borderRadius:1 }} />
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:".65rem", maxWidth:680 }}>
          {GOALS.map((g,i) => (
            <div key={i} className={"goal-row"+(done[i]?" done":"")} onClick={() => toggle(i)}>
              <div style={{ width:20, height:20, flexShrink:0, border:"1px solid #c9a84c", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:".6rem", color:"#c9a84c", marginTop:2, background:done[i]?"rgba(201,168,76,.2)":"transparent", transition:"background .22s" }}>{done[i] && "✓"}</div>
              <p className="serif" style={{ fontSize:"1.05rem", lineHeight:1.5 }}>{g}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const NAV = [
  { id:"home", label:"Home", icon:"🏠" },
  { id:"about-me", label:"About me", icon:"✦" },
  { id:"academia", label:"Academia", icon:"🎓", children:[
    { id:"academia/scholarship", label:"Scholarship", icon:"🏅" },
    { id:"academia/internships", label:"Internships", icon:"🔬" },
    { id:"academia/education", label:"Education", icon:"📚" },
    { id:"academia/projects", label:"Projects", icon:"⚗️" },
  ]},
  { id:"writing", label:"Writing", icon:"✍️", children:[
    { id:"writing/manuscript-i", label:"Manuscript I", icon:"📖" },
    { id:"writing/manuscript-ii", label:"Manuscript II", icon:"📝" },
    { id:"writing/book-of-poems", label:"Book of Poems", icon:"🌸" },
  ]},
  { id:"books", label:"Books", icon:"📚", children:[
    { id:"books/read", label:"Books I've Read", icon:"📖" },
    { id:"books/tbr", label:"My TBR", icon:"📋" },
    { id:"books/interesting", label:"Interesting Reads", icon:"💡" },
  ]},
  { id:"2026", label:"2026", icon:"🌟", children:[
    { id:"2026/goals", label:"My Goals", icon:"🎯" },
  ]},
];

export default function App() {
  const [page, setPage] = useState("home");
  const mainRef = useRef();
  const nav = p => { setPage(p); if (mainRef.current) mainRef.current.scrollTop = 0; };

  function renderPage() {
    switch (page) {
      case "home":                  return <HomePage nav={nav} />;
      case "about-me":              return <AboutPage />;
      case "academia":              return <AcademiaPage nav={nav} />;
      case "academia/scholarship":  return <div><PageHero eyebrow="✦ Academia" title={<em style={{ fontStyle:"italic", color:"#e8c97a" }}>Scholarship</em>} /><div style={{ padding:"2.5rem 3rem" }}><ComingSoon /></div></div>;
      case "academia/internships":  return <InternshipsPage />;
      case "academia/education":    return <div><PageHero eyebrow="✦ Academia" title={<em style={{ fontStyle:"italic", color:"#e8c97a" }}>Education</em>} /><div style={{ padding:"2.5rem 3rem" }}><ComingSoon /></div></div>;
      case "academia/projects":     return <ProjectsPage />;
      case "writing":               return <WritingPage nav={nav} />;
      case "writing/manuscript-i":  return <ManuscriptIPage />;
      case "writing/manuscript-ii": return <div><PageHero eyebrow="✦ Writing" title={<>Manuscript <em style={{ fontStyle:"italic", color:"#e8c97a" }}>II</em></>} /><div style={{ padding:"2.5rem 3rem" }}><ComingSoon /></div></div>;
      case "writing/book-of-poems": return <div><PageHero eyebrow="✦ Writing" title={<>Book of <em style={{ fontStyle:"italic", color:"#e8c97a" }}>Poems</em></>} /><div style={{ padding:"2.5rem 3rem" }}><ComingSoon /></div></div>;
      case "books":                 return <BooksPage nav={nav} />;
      case "books/read":            return <div><PageHero eyebrow="✦ Books" title={<>Books I've <em style={{ fontStyle:"italic", color:"#e8c97a" }}>Read</em></>} /><div style={{ padding:"2.5rem 3rem" }}><ComingSoon /></div></div>;
      case "books/tbr":             return <div><PageHero eyebrow="✦ Books" title={<>My <em style={{ fontStyle:"italic", color:"#e8c97a" }}>TBR</em></>} /><div style={{ padding:"2.5rem 3rem" }}><ComingSoon /></div></div>;
      case "books/interesting":     return <div><PageHero eyebrow="✦ Books" title={<em style={{ fontStyle:"italic", color:"#e8c97a" }}>Interesting Reads</em>} /><div style={{ padding:"2.5rem 3rem" }}><ComingSoon /></div></div>;
      case "2026":
      case "2026/goals":            return <Goals2026Page />;
      default:                      return <HomePage nav={nav} />;
    }
  }

  return (
    <div style={{ display:"flex", minHeight:"100vh", background:"#07090f", color:"#e6e0d6", fontFamily:"'DM Sans', sans-serif", fontWeight:300 }}>
      <style>{css}</style>
      <div style={{ position:"fixed", top:0, left:0, bottom:0, width:240, background:"rgba(7,9,15,.97)", borderRight:"1px solid rgba(201,168,76,0.16)", backdropFilter:"blur(20px)", display:"flex", flexDirection:"column", zIndex:50, overflowY:"auto" }}>
        <div style={{ padding:"1.8rem 1.5rem 1.4rem", borderBottom:"1px solid rgba(201,168,76,0.16)", cursor:"pointer" }} onClick={() => nav("home")}>
          <div className="serif" style={{ fontSize:"1.15rem", color:"#e8c97a", lineHeight:1.2 }}>Mariana Brites<br />Lameiro</div>
          <div style={{ fontSize:".58rem", letterSpacing:".2em", textTransform:"uppercase", color:"#7a7a8a", marginTop:".3rem" }}>✦ Made of stardust</div>
        </div>
        <nav style={{ padding:".7rem 0", flex:1 }}>
          {NAV.map(item => (
            <div key={item.id}>
              <div className={"nav-item"+(page===item.id?" active":"")} onClick={() => nav(item.id)}>
                <span style={{ fontSize:".82rem", width:"1rem", textAlign:"center" }}>{item.icon}</span>
                {item.label}
              </div>
              {item.children && (
                <div className="nav-child">
                  {item.children.map(ch => (
                    <div key={ch.id} className={"nav-item"+(page===ch.id?" active":"")} onClick={() => nav(ch.id)}>
                      <span style={{ fontSize:".78rem", width:"1rem", textAlign:"center" }}>{ch.icon}</span>
                      {ch.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        <div style={{ padding:"1rem 1.5rem", borderTop:"1px solid rgba(201,168,76,0.16)", fontSize:".68rem", color:"#7a7a8a", lineHeight:1.9 }}>
          <a href="https://linkedin.com/in/mariana-lameiro-7046a9274/" target="_blank" rel="noreferrer" style={{ color:"#c9a84c" }}>LinkedIn</a>
          {" · "}
          <a href="https://inkandallthings.substack.com" target="_blank" rel="noreferrer" style={{ color:"#c9a84c" }}>Substack</a>
          <br /><span style={{ fontSize:".6rem", opacity:.45 }}>All photos taken by Mariana</span>
        </div>
      </div>
      <div ref={mainRef} style={{ marginLeft:240, flex:1, minHeight:"100vh", overflowY:"auto" }}>
        <div key={page} className="fade-in">{renderPage()}</div>
      </div>
    </div>
  );
}